import { Add, ArrowDown, ArrowUp, TrashCan } from '@carbon/icons-react';
import {
  Button,
  IconButton,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Toggle,
} from '@carbon/react';
import {
  asProblem,
  crudApi,
  EmptyState,
  hasPermission,
  problemMessage,
  useAdmin,
  type RecordValues,
} from '@pos/admin-ui';
import type { ApiProblem } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ErrorNotice } from '../common/Notices';
import { SaveBar } from '../common/SaveBar';
import { TableLoading } from '../common/Loading';
import {
  addStep,
  changeCount,
  fromPreset,
  fromSteps,
  moveStep,
  problems,
  removeStep,
  STEP_TYPES,
  toRequest,
  updateStep,
  type SavedStep,
  type StepRow,
} from './flowDraft';
import './flows.scss';

async function loadSteps(id: string): Promise<SavedStep[]> {
  const api = crudApi<{ id: string; version: number | string; stepType: string; isRequired: boolean }, never>(
    hoApi,
    `/api/v1/menu/config/order-flows/${id}/steps`,
  );
  const page = await api.list({ page: 1, pageSize: 100 });
  return page.items.map((s) => ({ id: s.id, stepType: s.stepType, isRequired: s.isRequired }));
}

async function loadPresets() {
  const { data, error } = await hoApi.GET('/api/v1/menu/config/order-flow-presets');
  if (!data) throw error;
  return data;
}

/**
 * An order flow's steps as one editable list (FR-CFG-02, ADR 0024): change a step, mark it required or optional,
 * move it up or down, add or remove steps, or start over from a preset. Nothing is saved until Save, which sends
 * the whole list in one request.
 */
export function FlowStepsTab({ flow }: { flow: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, 'ho.config.order_flows.manage');
  const id = String(flow.id);
  const queryClient = useQueryClient();
  const key = ['ho', 'order-flow-steps', id];
  const steps = useQuery({ queryKey: key, queryFn: () => loadSteps(id) });
  const presets = useQuery({ queryKey: ['ho', 'order-flow-presets'], queryFn: loadPresets, enabled: canEdit });
  // The draft starts from the loaded steps, and again after each save or reload.
  const [draft, setDraft] = useState<{ source: unknown; rows: StepRow[] }>();
  const rows = draft && draft.source === steps.data ? draft.rows : steps.data ? fromSteps(steps.data) : [];
  const setRows = (next: (rows: StepRow[]) => StepRow[]) => setDraft({ source: steps.data, rows: next(rows) });
  const [adding, setAdding] = useState('');

  const found = problems(rows);
  const pending = steps.data ? changeCount(rows, steps.data) : 0;
  const used = new Set(rows.map((r) => r.stepType));
  const free = STEP_TYPES.filter((s) => s === 'prompt' || !used.has(s));
  const stepName = (type: string) => t(`flows.step.${type}` as I18nKey);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/menu/config/order-flows/{id}/steps', {
        params: { path: { id } },
        body: toRequest(rows),
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setDraft(undefined);
      queryClient.setQueryData(
        key,
        next.map((s) => ({ id: s.id, stepType: s.stepType, isRequired: s.isRequired })),
      );
    },
  });

  if (steps.isLoading) return <TableLoading columns={4} label={t('common.loading')} />;
  if (!steps.data)
    return <ErrorNotice title={problemMessage(t, asProblem(steps.error))} onRetry={() => void steps.refetch()} />;
  const conflict = (save.error as ApiProblem | null)?.code === 'common.version_conflict';

  return (
    <div className="pos-flow-steps">
      <p className="pos-flow-steps__hint">{t('flows.hint')}</p>

      <div className="pos-flow-steps__toolbar">
        {canEdit && presets.data && (
          <Select
            id="flow-preset"
            labelText={t('flows.preset.label')}
            value=""
            onChange={(e) => {
              const preset = presets.data?.find((p) => p.code === e.target.value);
              if (preset) setRows(() => fromPreset(preset.steps));
            }}
          >
            <SelectItem value="" text={t('flows.preset.placeholder')} />
            {presets.data.map((p) => (
              <SelectItem key={p.code} value={p.code} text={t('flows.preset.name', { en: p.name, vi: p.nameVi })} />
            ))}
          </Select>
        )}
      </div>

      <TableContainer>
        <Table aria-label={t('flows.steps')}>
          <TableHead>
            <TableRow>
              <TableHeader className="pos-flow-steps__number-col">{t('flows.columns.number')}</TableHeader>
              <TableHeader>{t('flows.columns.step')}</TableHeader>
              <TableHeader>{t('flows.columns.required')}</TableHeader>
              {canEdit && <TableHeader aria-label={t('admin.actions')} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, index) => (
              <TableRow key={r.key} className={r.id ? undefined : 'pos-flow-steps__row--new'}>
                <TableCell className="pos-flow-steps__number-col">{index + 1}</TableCell>
                <TableCell>
                  {canEdit ? (
                    <Select
                      id={`step-${r.key}`}
                      labelText={t('flows.stepNumber', { number: index + 1 })}
                      hideLabel
                      value={r.stepType}
                      invalid={found.has(r.key)}
                      invalidText={t(found.get(r.key) === 'acceptFirst' ? 'flows.problems.acceptFirst' : 'flows.problems.repeated')}
                      onChange={(e) => setRows((rs) => updateStep(rs, r.key, { stepType: e.target.value }))}
                    >
                      {STEP_TYPES.filter((s) => s === r.stepType || s === 'prompt' || !used.has(s)).map((s) => (
                        <SelectItem key={s} value={s} text={stepName(s)} />
                      ))}
                    </Select>
                  ) : (
                    stepName(r.stepType)
                  )}
                </TableCell>
                <TableCell>
                  <Toggle
                    id={`required-${r.key}`}
                    labelText={t('flows.requiredOf', { step: stepName(r.stepType) })}
                    hideLabel
                    labelA={t('flows.optional')}
                    labelB={t('flows.required')}
                    toggled={r.isRequired}
                    readOnly={!canEdit}
                    onToggle={(on) => setRows((rs) => updateStep(rs, r.key, { isRequired: on }))}
                  />
                </TableCell>
                {canEdit && (
                  <TableCell className="pos-admin-page__actions">
                    <IconButton
                      label={t('flows.moveUpOf', { step: stepName(r.stepType) })}
                      kind="ghost"
                      disabled={index === 0}
                      onClick={() => setRows((rs) => moveStep(rs, r.key, -1))}
                    >
                      <ArrowUp />
                    </IconButton>
                    <IconButton
                      label={t('flows.moveDownOf', { step: stepName(r.stepType) })}
                      kind="ghost"
                      disabled={index === rows.length - 1}
                      onClick={() => setRows((rs) => moveStep(rs, r.key, 1))}
                    >
                      <ArrowDown />
                    </IconButton>
                    <IconButton
                      label={t('flows.removeOf', { step: stepName(r.stepType) })}
                      kind="ghost"
                      onClick={() => setRows((rs) => removeStep(rs, r.key))}
                    >
                      <TrashCan />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {rows.length === 0 && <EmptyState heading={t('flows.emptyTitle')} description={t('flows.empty')} />}
      </TableContainer>

      {canEdit && free.length > 0 && (
        <div className="pos-flow-steps__add">
          <Select
            id="flow-add"
            labelText={t('flows.add')}
            hideLabel
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
          >
            <SelectItem value="" text={t('flows.addPlaceholder')} />
            {free.map((s) => (
              <SelectItem key={s} value={s} text={stepName(s)} />
            ))}
          </Select>
          <Button
            kind="tertiary"
            renderIcon={Add}
            disabled={!adding}
            onClick={() => {
              setRows((rs) => addStep(rs, adding));
              setAdding('');
            }}
          >
            {t('flows.add')}
          </Button>
        </div>
      )}

      {pending > 0 && (
        <SaveBar
          summary={t('flows.unsaved', { count: pending })}
          problems={[
            ...(rows.length === 0 ? [t('flows.problems.empty')] : []),
            ...([...found.values()].includes('repeated') ? [t('flows.problems.repeated')] : []),
            ...([...found.values()].includes('acceptFirst') ? [t('flows.problems.acceptFirst')] : []),
            ...(save.error ? [conflict ? t('flows.conflict') : problemMessage(t, asProblem(save.error))] : []),
          ]}
          discardText={t('flows.discard')}
          saveText={t('flows.save', { count: pending })}
          saveDisabled={found.size > 0 || rows.length === 0}
          saving={save.isPending}
          onDiscard={() => {
            save.reset();
            setDraft(undefined);
            if (conflict) void steps.refetch();
          }}
          onSave={() => save.mutate()}
        />
      )}
    </div>
  );
}
